import * as React from 'react'
import cx from 'classnames'
import styles from './SideSheet.sass'
import { useViewportSize } from 'hooks/viewport-hooks'
import { Breakpoints } from 'consts/breakpoints'

/**
 * Side sheet elevation variant.
 */
export const enum SideSheetElevation {
  /**
   * Always visible and takes up space.
   */
  Coplanar = 'coplanar',
  /**
   * Above the content
   */
  Above = 'above',
}

interface SideSheetHostViewProps {
  elevation?: SideSheetElevation
  children?: React.ReactNode
  sheetVisible?: boolean
  mainless?: boolean
  onRequestHide?: () => unknown
}

/**
 * The host view, used to host the primary UI region and
 * a side sheet.
 */
export const SideSheetHostView = React.forwardRef(function SideSheetHostView(
  {
    children,
    onRequestHide,
    sheetVisible,
    mainless,
    elevation = SideSheetElevation.Coplanar,
  }: SideSheetHostViewProps,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <div
      ref={ref}
      className={cx(
        styles.root,
        mainless && styles.mainless,
        styles[elevation],
        sheetVisible && styles.visible
      )}
    >
      {children}
      <div className={styles.overlay} onClick={onRequestHide}></div>
    </div>
  )
})

interface SideSheetMainProps {
  children?: React.ReactNode
}

/**
 * The main UI region.
 */
export function SideSheetMain({ children }: SideSheetMainProps) {
  return <div className={cx(styles.main)}>{children}</div>
}

interface SideSheetProps {
  children?: React.ReactNode
}

/**
 * The actual side sheet itself.
 */
export function SideSheet({ children }: SideSheetProps) {
  return <div className={cx(styles.sheet)}>{children}</div>
}

/**
 * Convenience hook for managing the side sheet state.
 */
export function useSideSheet() {
  const [visible, setVisible] = React.useState(false)
  const viewportSize = useViewportSize()
  const show = React.useCallback(() => setVisible(true), [])
  const hide = React.useCallback(() => setVisible(false), [])
  const shouldBeAbove = viewportSize.width < Breakpoints.collapseSidebar
  const toggle = React.useCallback(
    () => (visible ? hide() : show()),
    [shouldBeAbove, visible, hide]
  )
  return {
    show,
    hide,
    toggle,
    shouldBeAbove,
    visible,
    elevation: shouldBeAbove
      ? SideSheetElevation.Above
      : SideSheetElevation.Coplanar,
  }
}
